import { ReactNode, useEffect, useRef, useCallback } from "react";
import ChevronLeftIcon from "../../icons/chevronLeft.icon";
import ChevronRightIcon from "../../icons/chevronRight.icon";

export default function ProductSlider({
  className,
  iconClassName,
  children,
}: {
  className?: string;
  iconClassName?: string;
  children: ReactNode;
}) {
  const gridRef = useRef<HTMLDivElement>(null);

  // Helper function to get the offset width of the first child
  const getOffset = useCallback(() => {
    if (!gridRef.current || gridRef.current.childNodes.length === 0) return 0;
    return (gridRef.current.childNodes[0] as HTMLElement).offsetWidth;
  }, []);

  // Scroll functions using useCallback to maintain reference stability
  const scrollLeft = useCallback(() => {
    if (gridRef.current === null || gridRef.current.childNodes.length === 0) return;
    gridRef.current.scrollLeft -= getOffset();
  }, [getOffset]);

  const scrollRight = useCallback(() => {
    if (gridRef.current === null || gridRef.current.childNodes.length === 0) return;
    gridRef.current.scrollLeft += getOffset();
  }, [getOffset]);

  // Auto scroll function wrapped in useCallback
  const autoScroll = useCallback(() => {
    scrollRight();
  }, [scrollRight]);

  // Click handlers
  const scrollLeftClick = useCallback(() => {
    scrollLeft();
  }, [scrollLeft]);

  const scrollRightClick = useCallback(() => {
    scrollRight();
  }, [scrollRight]);

  // Set up auto-scroll interval with proper dependencies
  useEffect(() => {
    const interval = setInterval(() => {
      autoScroll();
    }, 10000);

    return () => clearInterval(interval);
  }, [autoScroll]); // autoScroll is now a stable dependency

  return (
    <>
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      <div className={`relative flex items-center justify-center px-6 md:px-16 ${className || ""}`}>
        <div
          ref={gridRef}
          className="w-full flex justify-start gap-4 md:gap-6 snap-x scroll-smooth overflow-x-scroll scrollbar-hide py-2"
        >
          {children}
        </div>
        <button
          className="absolute top-1/2 left-0 md:left-2 -translate-y-1/2 cursor-pointer hover:opacity-75 transition-opacity"
          onClick={scrollLeftClick}
        >
          <ChevronLeftIcon className={`w-6 h-6 md:w-6 md:h-6 ${iconClassName || ""}`} />
        </button>
        <button
          className="absolute top-1/2 right-0 md:right-2 -translate-y-1/2 cursor-pointer hover:opacity-75 transition-opacity"
          onClick={scrollRightClick}
        >
          <ChevronRightIcon className={`w-6 h-6 md:w-6 md:h-6 ${iconClassName || ""}`} />
        </button>
      </div>
    </>
  );
}
